import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Clock, Check, MessageCircle, ArrowUpRight } from "lucide-react";
import { Reveal, Overline } from "@/components/Reveal";
import { TREATMENTS, whatsappLink } from "@/lib/data";

const spanFor = (i) => (i < 2 ? "lg:col-span-3" : "lg:col-span-2");

const Treatments = () => {
  const [selected, setSelected] = useState(null);

  return (
    <section id="trattamenti" data-testid="treatments-section" className="bg-[#FAF8F5] py-28 lg:py-36">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <Reveal className="flex flex-wrap items-end justify-between gap-6 mb-16">
          <div>
            <Overline>02 — Trattamenti Specialistici</Overline>
            <h2 className="mt-5 text-3xl sm:text-4xl lg:text-5xl font-light tracking-tight text-[#1A2E2A] leading-[1.1]">
              I Trattamenti <span className="italic text-[#489A88]">Disponibili</span>
            </h2>
          </div>
          <p className="text-sm sm:text-base text-[#64748B] max-w-sm leading-relaxed">
            Ogni seduta è preceduta da una valutazione iniziale per adattare il trattamento alle reali necessità del tuo corpo.
          </p>
        </Reveal>

        <div className="grid md:grid-cols-2 lg:grid-cols-6 gap-6">
          {TREATMENTS.map((t, i) => (
            <Reveal key={t.id} delay={i * 0.08} className={spanFor(i)}>
              <motion.button
                data-testid={`treatment-card-${t.id}`}
                onClick={() => setSelected(t)}
                whileHover={{ y: -8 }}
                transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                className="group w-full text-left rounded-[1.75rem] bg-white border border-[#E2E8F0]/70 overflow-hidden shadow-[0_10px_40px_-20px_rgba(26,46,42,0.2)] hover:shadow-[0_30px_60px_-25px_rgba(72,154,136,0.4)] transition-shadow duration-500"
              >
                <div className="relative h-52 overflow-hidden">
                  <img
                    src={t.image}
                    alt={t.title}
                    className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#1A2E2A]/35 to-transparent opacity-60" />
                  <span className="absolute top-4 left-4 rounded-full bg-white/90 backdrop-blur px-3.5 py-1.5 text-xs font-semibold text-[#1A2E2A] flex items-center gap-1.5">
                    <Clock className="w-3.5 h-3.5 text-[#489A88]" />
                    {t.duration}
                  </span>
                  <span className="absolute top-4 right-4 w-9 h-9 rounded-full bg-white/90 backdrop-blur flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <ArrowUpRight className="w-4 h-4 text-[#2D6D60]" />
                  </span>
                </div>
                <div className="p-7">
                  <div className="flex items-start justify-between gap-4">
                    <h3 className="font-['Outfit'] text-xl font-medium text-[#1A2E2A] leading-snug">{t.title}</h3>
                    <span className="shrink-0 font-['Outfit'] text-lg font-medium text-[#489A88]">{t.price}</span>
                  </div>
                  <p className="mt-3 text-sm leading-relaxed text-[#64748B]">{t.short}</p>
                </div>
              </motion.button>
            </Reveal>
          ))}
        </div>
      </div>

      <AnimatePresence>
        {selected && (
          <motion.div
            data-testid="treatment-modal"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[70] bg-[#1A2E2A]/50 backdrop-blur-sm flex items-center justify-center p-4 sm:p-8"
            onClick={() => setSelected(null)}
          >
            <motion.div
              initial={{ opacity: 0, y: 48, scale: 0.96 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 32, scale: 0.97 }}
              transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-2xl bg-white rounded-[2rem] overflow-hidden shadow-2xl max-h-[90vh] overflow-y-auto"
            >
              <div className="relative h-64">
                <img src={selected.image} alt={selected.title} className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/45 to-transparent" />
                <button
                  data-testid="treatment-modal-close"
                  onClick={() => setSelected(null)}
                  className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/90 backdrop-blur flex items-center justify-center text-[#1A2E2A] hover:bg-white transition-colors"
                  aria-label="Chiudi dettaglio trattamento"
                >
                  <X className="w-5 h-5" />
                </button>
                <div className="absolute bottom-5 left-7 right-7 flex items-end justify-between gap-4">
                  <h3 className="font-['Outfit'] text-2xl sm:text-3xl font-medium text-white">{selected.title}</h3>
                </div>
              </div>
              <div className="p-7 sm:p-9">
                <div className="flex flex-wrap gap-3">
                  <span className="rounded-full bg-[#E8F5F2] text-[#2D6D60] text-xs font-semibold px-4 py-2 flex items-center gap-1.5">
                    <Clock className="w-3.5 h-3.5" /> {selected.duration}
                  </span>
                  <span className="rounded-full bg-[#FAF8F5] border border-[#E2E8F0] text-[#1A2E2A] text-xs font-semibold px-4 py-2">
                    {selected.price}{selected.price.startsWith("€") ? " · IVA inclusa" : ""}
                  </span>
                </div>
                <p className="mt-6 text-base leading-relaxed text-[#4A5568]">{selected.long}</p>
                <p className="mt-5 flex items-center gap-2 text-sm font-medium text-[#2D6D60]">
                  <Check className="w-4 h-4" /> {selected.included}
                </p>
                <a
                  href={whatsappLink(`Buongiorno Francesca, vorrei prenotare un trattamento: ${selected.title}. Potremmo sentirci?`)}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="treatment-modal-whatsapp"
                  className="mt-8 inline-flex w-full sm:w-auto items-center justify-center gap-2.5 rounded-full bg-[#489A88] text-white font-medium px-8 py-4 hover:bg-[#2D6D60] transition-colors duration-300"
                >
                  <MessageCircle className="w-5 h-5" />
                  Prenota questo trattamento
                </a>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default Treatments;
